/**
 * @module root/shared
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */ /** */
/* tslint:disable:no-any */
import { RepProfileResponse } from '@support/api/proxies/responses/api-998.interface';
import { RepProfileRequest } from '@support/api/proxies/requests/api-998.interface';
import { StaticModal } from '@support/shared/widgets/interfaces/static-modal.interface';
import { Injectable, Inject, EventEmitter } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { CommonUtilService } from '@support/shared/services/common-util.service';
import { REPPROFILE_CONSTANTS } from './../../../root/shared/constants/rep-profile.constants';
import { TmoButton } from '@support/shared/widgets/interfaces/button.interface';
import { RepdashStoreUtilsService } from '@support/repdash-store-utilities/utils/repdash-store-utils.service';
import { MatchmediaService } from '@core/services/matchmedia.service';

@Injectable()
export class RepProfileService {
  /**
   * Gets the user id from the cookies
   */
  public repId: string;
  /**
   * Stores the data of type RepProfileRequest
   */
  public data: RepProfileRequest;
  /**
   * Stores the string name value for oamCookieName
   */
  private oamCookieName: string;
  /**
   * Stores the value for oamCookie
   */
  private oamCookieValue: string;

  constructor(
    @Inject(DOCUMENT) private document: any,
    private commonUtils: CommonUtilService,
    private repdashStoreUtilsService: RepdashStoreUtilsService,
    private matchmediaService: MatchmediaService
  ) {
    this.repId = this.getCookie('userid');
  }

  /**
   * Gets the remaining days to password expiry
   * @returns number
   */
  public getRemainingDaysToPasswordExpiry(): number {
    let lastPasswordResetDate = Number(this.getCookie('passwordexpiry'));
    let passwordResetDate;
    if (this.commonUtils.isNotEmpty(lastPasswordResetDate)) {
      lastPasswordResetDate = Math.round(lastPasswordResetDate / REPPROFILE_CONSTANTS.ONE_MS);
      lastPasswordResetDate -= REPPROFILE_CONSTANTS.EPOCH_DATE;
      passwordResetDate = new Date(
        lastPasswordResetDate * REPPROFILE_CONSTANTS.MILLISECONDS_PER_SECOND
      );
    }
    const currentDate: any = new Date();
    const dateDiffTimeStamp = currentDate - passwordResetDate;
    const dateDiffInDays: number = Math.round(
      dateDiffTimeStamp /
        (REPPROFILE_CONSTANTS.MILLISECONDS_PER_SECOND *
          REPPROFILE_CONSTANTS.SECONDS_PER_HOUR *
          REPPROFILE_CONSTANTS.HOURS_PER_DAY)
    );
    return dateDiffInDays;
  }

  // PR78046 - REMOVING UNUSED CODE FOR ROLE SELECTOR BLOCK.

  /**
   * Gets static modal data
   * @returns apiErrorModalData<StaticModal>
   */

  public getApiErrorModal(): StaticModal {
    const modalbuttons: TmoButton[] = [];
    const button = this.setButtons();
    modalbuttons.push(button);
    const apiErrorModalData: StaticModal = <StaticModal>{};
    apiErrorModalData.disableDismiss = false;
    apiErrorModalData.disableEsc = true;
    apiErrorModalData.buttons = modalbuttons;
    apiErrorModalData.toggleModalEmitter = new EventEmitter();
    apiErrorModalData.dismissableMask = true;
    apiErrorModalData.modalName = 'api-error-modal';
    return apiErrorModalData;
  }

  /**
   * Create buttons array for static modal
   * @returns TmoButton[]
   */
  public createStaticModalButtons(
    showRetryButton: Boolean,
    closeButtonText: string,
    retryButtonText?: string
  ): TmoButton[] {
    let modalbuttons: TmoButton[] = [];
    const button = this.setButtons();
    button.name = closeButtonText;
    modalbuttons.push(button);
    if (showRetryButton) {
      modalbuttons = [];
      button.class = REPPROFILE_CONSTANTS.SECONDRY_BUTTON_CLASS;
      button.id = REPPROFILE_CONSTANTS.CLOSE_BUTTON_ID;
      button.type = REPPROFILE_CONSTANTS.SECONDRY_BUTTON_TYPE;
      button.name = closeButtonText;
      modalbuttons.push(button);
      const retrybutton: TmoButton = <TmoButton>{};
      retrybutton.class = REPPROFILE_CONSTANTS.PRIMARY_BUTTON_CLASS;
      retrybutton.id = REPPROFILE_CONSTANTS.RETRY_BUTTON_ID;
      retrybutton.type = REPPROFILE_CONSTANTS.PRIMARY_BUTTON_TYPE;
      retrybutton.name = retryButtonText;
      modalbuttons.push(retrybutton);
    }
    return modalbuttons;
  }

  /**
   * Set profile information
   * @returns TmoButton[]
   */
  public getProfileInformation(): RepProfileResponse {
    const profileInformation = this.repdashStoreUtilsService.getDate('app.repProfile');
    profileInformation.responsiveState = this.matchmediaService.isDesktop()
      ? 'Desktop'
      : this.matchmediaService.isPhone()
        ? 'Mobile'
        : 'Tablet';
    return profileInformation;
  }

  /**
   * Method to get the cookie value
   * @param cookieName Stores the name of the cookie
   * @returns string
   */
  private getCookie(cookieName: string): string {
    const match = document.cookie.match(new RegExp(`${cookieName}=([^;]+)`));
    if (match) {
      return match[1];
    }
  }

  /**
   * Method to set static button value
   * @returns TmoButton
   */
  private setButtons(): TmoButton {
    const button: TmoButton = <TmoButton>{};
    button.class = REPPROFILE_CONSTANTS.PRIMARY_BUTTON_CLASS;
    button.id = REPPROFILE_CONSTANTS.CLOSE_BUTTON_ID;
    button.type = REPPROFILE_CONSTANTS.PRIMARY_BUTTON_TYPE;
    return button;
  }
  // PR78046 - REMOVING UNUSED CODE FOR ROLE SELECTOR BLOCK.
}
